import api from './api';
import { ISignatureUploadResponse } from '../types/form';
import {
  IExecutorVerification,
  IDocumentSummary,
  IDocumentDetail,
  IDocumentRevision,
  DocumentContent,
  IDocumentImport,
  IDocumentDraft,
  IDocumentFeedback,
  ITemplatePreviewResponse,
} from '../types/document';
import { IQuestionnaireSubmission } from '../types/questionnaire';

export const documentService = {
  // ============ Multi-party Signature Methods ============

  /**
   * Upload a signature image to GCS.
   * @param token - The assignment token
   * @param signatureType - 'user_signature' | 'lawyer_signature' | 'executor_signature'
   * @param file - The image File object
   */
  uploadSignature: async (
    token: string,
    signatureType: 'user_signature' | 'lawyer_signature' | 'executor_signature',
    file: File,
    contentHash: string
  ): Promise<ISignatureUploadResponse> => {
    const formData = new FormData();
    formData.append('file', file, 'signature.png');
    formData.append('signatureType', signatureType);
    formData.append('token', token);
    formData.append('contentHash', contentHash);

    const response = await api.post<ISignatureUploadResponse>(
      '/signatures/upload',
      formData
    );
    return response.data;
  },

  /**
   * Remove a signature image.
   * @param token - The assignment token
   * @param signatureType - 'user_signature' | 'lawyer_signature' | 'executor_signature'
   */
  removeSignature: async (
    token: string,
    signatureType: 'user_signature' | 'lawyer_signature' | 'executor_signature',
    contentHash: string
  ): Promise<{ message: string }> => {
    const response = await api.delete<{ message: string }>(
      '/signatures/remove',
      { body: JSON.stringify({ token, signatureType, contentHash }) }
    );
    return response.data;
  },

  getAssignedDocumentById: async (documentId: string): Promise<IQuestionnaireSubmission> => {
    const response = await api.get<IQuestionnaireSubmission>(`/documents/${documentId}/assigned-detail`);
    return response.data;
  },

  getAssignedDocumentByToken: async (token: string): Promise<IQuestionnaireSubmission> => {
    const response = await api.get<IQuestionnaireSubmission>(`/documents/assignments/${token}`);
    return response.data;
  },

  // ============ Estate Executor Assignment ============

  /**
   * Create or replace the executor assignment on a document. Idempotent.
   */
  assignExecutor: async (
    documentId: string,
    payload: { executorEmail: string; expiresInDays: number }
  ): Promise<void> => {
    await api.put(`/documents/${documentId}/executor-assignment`, payload);
  },

  /**
   * Executor confirms they have reviewed the document they were assigned to.
   */
  verifyExecutorAssignment: async (token: string, contentHash: string): Promise<IExecutorVerification> => {
    const response = await api.post<IExecutorVerification>(`/documents/assignments/${token}/verify`, { contentHash });
    return response.data;
  },

  /**
   * List all registered Estate Executors.
   */
  getExecutors: async (): Promise<Array<{ _id: string; name: string; email: string; phoneNumber?: string }>> => {
    const response = await api.get<Array<{ _id: string; name: string; email: string; phoneNumber?: string }>>('/documents/executors');
    return response.data;
  },

  // ============ Document Intake, History, and Revision API ============

  getDocuments: async (params?: { view?: string; search?: string }): Promise<IDocumentSummary[]> => {
    const response = await api.get<any[]>('/documents', { params });
    return response.data.map((doc: any) => ({
      id: doc.id || doc._id,
      title: doc.title || doc.templateTitle || 'Untitled Document',
      sourceType: doc.sourceType || 'legacy_template',
      sourceKey: doc.sourceKey,
      source: doc.source,
      revisionNumber: doc.revisionNumber || doc.version || 1,
      currentRevisionId: doc.currentRevisionId,
      finalized: doc.finalized ?? false,
      finalizedAt: doc.finalizedAt,
      userEmail: doc.userEmail,
      createdAt: doc.createdAt,
      updatedAt: doc.updatedAt,
      permissions: doc.permissions || {
        canView: true,
        canEdit: !doc.finalized,
        canShare: !doc.finalized && !(
          (doc.sourceType || 'legacy_template') === 'questionnaire' &&
          Boolean(doc.source?.createdByLawyer || doc.source?.lawyerId)
        ),
      },
      review: doc.review,
    }));
  },

  getDocumentById: async (id: string, version?: number): Promise<IDocumentDetail> => {
    const response = await api.get<any>(`/documents/${id}`, {
      params: version !== undefined ? { version: String(version) } : undefined,
    });
    const doc = response.data;
    return {
      id: doc.id || doc._id,
      title: doc.title || doc.templateTitle || 'Untitled Document',
      sourceType: doc.sourceType || 'legacy_template',
      source: doc.source,
      currentRevision: doc.currentRevision,
      contentHash: doc.contentHash || '',
      permissions: doc.permissions || { canView: true, canEdit: false, canShare: false },
      review: doc.review,
      finalized: doc.finalized ?? false,
      finalizedAt: doc.finalizedAt,
      createdAt: doc.createdAt,
      updatedAt: doc.updatedAt,
      userEmail: doc.userEmail,
      templateTitle: doc.templateTitle,
      templateText: doc.templateText,
      fields: doc.fields,
      responses: doc.responses,
    };
  },

  deleteDocument: async (id: string): Promise<void> => {
    await api.delete(`/documents/${id}`);
  },

  updateDocumentContent: async (
    id: string,
    data: { baseRevisionId?: string; contentHash?: string; title?: string; content: DocumentContent }
  ): Promise<any> => {
    const response = await api.patch(`/documents/${id}`, data);
    return response.data;
  },

  getDocumentRevisions: async (id: string): Promise<IDocumentRevision[]> => {
    const response = await api.get<IDocumentRevision[]>(`/documents/${id}/revisions`);
    return response.data;
  },

  getDocumentRevisionById: async (id: string, revisionId: string): Promise<IDocumentRevision> => {
    const response = await api.get<IDocumentRevision>(`/documents/${id}/revisions/${revisionId}`);
    return response.data;
  },

  shareDocumentWithLawyer: async (
    id: string,
    data: { lawyerId: string; baseRevisionId?: string; extractionReviewed?: boolean }
  ): Promise<{ message: string; assignmentId: string; lawyerEmail: string; lawyerName?: string }> => {
    const response = await api.post(`/documents/${id}/share-lawyer`, data);
    return response.data;
  },

  revokeLawyerShare: async (id: string, shareId: string): Promise<{ message: string }> => {
    const response = await api.delete(`/documents/${id}/lawyer-shares/${shareId}`);
    return response.data;
  },

  uploadDocumentImport: async (options: { file: File; title?: string; idempotencyKey?: string }): Promise<any> => {
    const formData = new FormData();
    formData.append('file', options.file);
    if (options.title && options.title.trim()) {
      formData.append('title', options.title.trim());
    }
    if (options.idempotencyKey) {
      formData.append('idempotencyKey', options.idempotencyKey);
    }
    const response = await api.post('/document-imports', formData);
    return response.data;
  },

  getDocumentImports: async (): Promise<IDocumentImport[]> => {
    const response = await api.get<{ imports: IDocumentImport[] }>('/document-imports');
    return response.data.imports || [];
  },

  createManualDocument: async (data: { title?: string; text: string; editorContent?: unknown }): Promise<IDocumentDetail> => {
    const response = await api.post('/documents/manual', data);
    return response.data;
  },

  getTemplatePreview: async (
    documentId: string,
    data: { templateId: string; templateVersion?: number; baseRevisionId?: string }
  ): Promise<ITemplatePreviewResponse> => {
    const response = await api.post<ITemplatePreviewResponse>(`/documents/${documentId}/template-preview`, data);
    return response.data;
  },

  getDocumentDraft: async (documentId: string): Promise<{ draft: IDocumentDraft | null }> => {
    const response = await api.get<{ draft: IDocumentDraft | null }>(`/documents/${documentId}/draft`);
    return response.data;
  },

  saveDocumentDraft: async (documentId: string, data: any): Promise<{ draft: IDocumentDraft }> => {
    const response = await api.put<{ draft: IDocumentDraft }>(`/documents/${documentId}/draft`, data);
    return response.data;
  },

  publishDocument: async (
    documentId: string,
    data: any
  ): Promise<{ success: boolean; documentId: string; revisionId: string; revisionNumber: number; contentHash: string }> => {
    const response = await api.post(`/documents/${documentId}/publish`, data);
    return response.data;
  },

  getDocumentFeedback: async (documentId: string): Promise<{ feedback: IDocumentFeedback[] }> => {
    const response = await api.get<{ feedback: IDocumentFeedback[] }>(`/documents/${documentId}/feedback`);
    return response.data;
  },

  createDocumentFeedback: async (
    documentId: string,
    data: { revisionId: string; contentHash: string; message: string }
  ): Promise<{ feedback: IDocumentFeedback }> => {
    const response = await api.post<{ feedback: IDocumentFeedback }>(`/documents/${documentId}/feedback`, data);
    return response.data;
  },
};
